import {
  T_WA_LOG_MANUALLY_ADDED,
  T_WA_LOG_MANUALLY_DELETED,
  T_WA_LOG_MANUALLY_EDITED,
} from 'core/const/tracker';
import { applyTimeZone, Locale } from 'core/helpers/dateUtils';
import ActivityTimelineService from 'core/services/service.activityTimeline';
import { useToast } from 'library/atoms/Toast';
import moment from 'moment';
import { useState } from 'react';
import { AppTracker } from 'shared/analytics/tracker';
import { useOrganizationConfig } from 'views/main/organization/schedules';

export const useManualActivityTimelineUpdate = (incidentId: string) => {
  const activityTimelineSevice = new ActivityTimelineService(incidentId);
  const [savingData, setSavingData] = useState<boolean>(false);
  const [deletingEntry, setDeletingEntry] = useState<boolean>(false);
  const [error, setError] = useState(undefined);

  const timeZone = useOrganizationConfig().organization.currentUser.u?.time_zone ?? 'utc';

  function extractDateAndTime(isoString: string) {
    const date = moment(isoString, 'DD/MM/YYYY, hh:mm:ss A').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'); // Full ISO format
    const time = moment(isoString, 'DD/MM/YYYY, hh:mm:ss A').format('HH:mm:ss'); // Extract only time (24-hour format)

    return { date, time };
  }

  const toast = useToast();

  const addActivityEvent = async (
    content: string,
    timeStamp: string,
    showInPostmortem: boolean,
    cb: () => void,
  ) => {
    try {
      setSavingData(true);
      const response = await activityTimelineSevice.addManualActivityInTimeline(
        content,
        applyTimeZone(
          extractDateAndTime(timeStamp).date,
          extractDateAndTime(timeStamp).time,
          timeZone,
        ),
        showInPostmortem,
      );
      if (response.data) {
        AppTracker.track(T_WA_LOG_MANUALLY_ADDED, {
          'Incident ID': incidentId,
          'Show in Postmortem': showInPostmortem,
          'Event Timestamp': timeStamp,
        });
        cb();
        toast({
          status: 'success',
          text: `Timeline has been successfully updated`,
        });
      }
    } catch (error: any) {
      setError(error.response?.data?.meta?.error_message);
      toast({
        status: 'error',
        text: error.response?.data?.meta?.error_message,
      });
    } finally {
      setSavingData(false);
    }
  };

  const deleteEntry = async (logId: string, cb: () => void) => {
    try {
      setDeletingEntry(true);
      const res = await activityTimelineSevice.deleteEntryInActivityTimeline(logId);
      if (res?.data) {
        AppTracker.track(T_WA_LOG_MANUALLY_DELETED, {
          'Incident ID': incidentId,
        });
        toast({
          status: 'success',
          text: `Log has been successfully deleted`,
        });
        cb();
      }
    } catch (error: any) {
      setError(error.response?.data?.meta?.error_message);

      toast({
        status: 'error',
        text: error.response?.data?.meta?.error_message,
      });
    } finally {
      setDeletingEntry(false);
    }
  };

  const updateEntryInActivityTimeline = async (
    logId: string,
    content: string,
    timestamp: string,
    showInPostmortem: boolean,
    cb: () => void,
  ) => {
    try {
      setSavingData(true);
      const res = await activityTimelineSevice.updateEntryInActivityTimeline(
        logId,
        content,
        applyTimeZone(
          extractDateAndTime(timestamp).date,
          extractDateAndTime(timestamp).time,
          timeZone,
        ),
        showInPostmortem,
      );
      if (res?.data) {
        AppTracker.track(T_WA_LOG_MANUALLY_EDITED, {
          'Incident ID': incidentId,
        });
        cb();
        toast({
          status: 'success',
          text: `Timeline has been successfully updated`,
        });
      }
    } catch (error: any) {
      setError(error.response?.data?.meta?.error_message);
      toast({
        status: 'error',
        text: error.response?.data?.meta?.error_message,
      });
    } finally {
      setSavingData(false);
    }
  };

  return {
    addActivityEvent,
    deleteEntry,
    updateEntryInActivityTimeline,
    deletingEntry,
    savingData,
    error,
    setError,
  };
};
